import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { Row, Col, Button, Form, Modal } from "react-bootstrap";
import BillFour from "./billfour/BillFour";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import { SketchPicker } from 'react-color';
import { Tooltip } from 'react-tooltip';
import NotifyData from "../../components/NotifyData";
import { uploadCraftDesignFileApi, addCraftDesignApi, updateCraftDesignApi, fetchCraftDesignExcelApi } from "../../services/CraftDesignService";
import { SERVER_BASE_URL } from "../../config/API";
import './ThemeAdjustments.css';

const PREDEFINED_DUAL_THEMES = [
  {
    id: "black_gold",
    name: "Classic Crackers (Black & Gold)",
    primary: "#000000",
    secondary: "#FFD54F",
    headerBg: "#000000",
    headerText: "#FFFFFF",
    categoryBg: "#000000",
    categoryText: "#FFD54F",
    footerBg: "#000000",
    footerText: "#FFD54F",
    tableBg: "#FFFFFF",
    altTableBg: "#FFFDF0",
    borderColor: "#000000",
  },
  {
    id: "navy_amber",
    name: "Navy Blue & Amber Gold",
    primary: "#0A2540",
    secondary: "#FFC107",
    headerBg: "#0A2540",
    headerText: "#FFFFFF",
    categoryBg: "#0A2540",
    categoryText: "#FFC107",
    footerBg: "#0A2540",
    footerText: "#FFC107",
    tableBg: "#FFFFFF",
    altTableBg: "#F0F4F8",
    borderColor: "#0A2540",
  },
  {
    id: "crimson_yellow",
    name: "Crimson Red & Bright Yellow",
    primary: "#8B0000",
    secondary: "#FFD700",
    headerBg: "#8B0000",
    headerText: "#FFFFFF",
    categoryBg: "#8B0000",
    categoryText: "#FFD700",
    footerBg: "#8B0000",
    footerText: "#FFD700",
    tableBg: "#FFFFFF",
    altTableBg: "#FFF8E7",
    borderColor: "#8B0000",
  },
  {
    id: "royal_purple_gold",
    name: "Royal Purple & Gold",
    primary: "#4A154B",
    secondary: "#FACC15",
    headerBg: "#4A154B",
    headerText: "#FFFFFF",
    categoryBg: "#4A154B",
    categoryText: "#FACC15",
    footerBg: "#4A154B",
    footerText: "#FACC15",
    tableBg: "#FFFFFF",
    altTableBg: "#FAF5FF",
    borderColor: "#4A154B",
  },
  {
    id: "emerald_mint",
    name: "Emerald Green & Mint",
    primary: "#004D40",
    secondary: "#A7F3D0",
    headerBg: "#004D40",
    headerText: "#FFFFFF",
    categoryBg: "#004D40",
    categoryText: "#A7F3D0",
    footerBg: "#004D40",
    footerText: "#A7F3D0",
    tableBg: "#FFFFFF",
    altTableBg: "#ECFDF5",
    borderColor: "#004D40",
  },
  {
    id: "maroon_amber",
    name: "Deep Maroon & Amber",
    primary: "#581845",
    secondary: "#FFC300",
    headerBg: "#581845",
    headerText: "#FFFFFF",
    categoryBg: "#581845",
    categoryText: "#FFC300",
    footerBg: "#581845",
    footerText: "#FFC300",
    tableBg: "#FFFFFF",
    altTableBg: "#FFFBEB",
    borderColor: "#581845",
  },
  {
    id: "ocean_cyan",
    name: "Ocean Blue & Cyan",
    primary: "#0369A1",
    secondary: "#38BDF8",
    headerBg: "#0369A1",
    headerText: "#FFFFFF",
    categoryBg: "#0369A1",
    categoryText: "#38BDF8",
    footerBg: "#0369A1",
    footerText: "#38BDF8",
    tableBg: "#FFFFFF",
    altTableBg: "#F0F9FF",
    borderColor: "#0369A1",
  },
  {
    id: "chocolate_gold",
    name: "Rich Chocolate & Yellow",
    primary: "#451A03",
    secondary: "#FEF08A",
    headerBg: "#451A03",
    headerText: "#FFFFFF",
    categoryBg: "#451A03",
    categoryText: "#FEF08A",
    footerBg: "#451A03",
    footerText: "#FEF08A",
    tableBg: "#FFFFFF",
    altTableBg: "#FFFBEB",
    borderColor: "#451A03",
  },
  {
    id: "charcoal_white",
    name: "Midnight Charcoal & White",
    primary: "#18181B",
    secondary: "#FFFFFF",
    headerBg: "#18181B",
    headerText: "#FFFFFF",
    categoryBg: "#18181B",
    categoryText: "#FFFFFF",
    footerBg: "#18181B",
    footerText: "#FFFFFF",
    tableBg: "#FFFFFF",
    altTableBg: "#F4F4F5",
    borderColor: "#18181B",
  },
  {
    id: "ruby_silver",
    name: "Ruby Red & Silver",
    primary: "#991B1B",
    secondary: "#E2E8F0",
    headerBg: "#991B1B",
    headerText: "#FFFFFF",
    categoryBg: "#991B1B",
    categoryText: "#E2E8F0",
    footerBg: "#991B1B",
    footerText: "#E2E8F0",
    tableBg: "#FFFFFF",
    altTableBg: "#FEF2F2",
    borderColor: "#991B1B",
  },
];

// Helper to extract dominant/primary accent color from an image Data URL / URL using HTML Canvas
const extractImageColor = (imageSrc) => {
  return new Promise((resolve) => {
    if (!imageSrc) return resolve(null);
    const img = new window.Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 100;
        canvas.height = 100;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, 100, 100);
        const imgData = ctx.getImageData(0, 0, 100, 100).data;
        let r = 0, g = 0, b = 0, count = 0;
        for (let i = 0; i < imgData.length; i += 4) {
          const alpha = imgData[i + 3];
          if (alpha > 50) {
            r += imgData[i];
            g += imgData[i + 1];
            b += imgData[i + 2];
            count++;
          }
        }
        if (count > 0) {
          r = Math.round(r / count);
          g = Math.round(g / count);
          b = Math.round(b / count);
          const hex = "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
          resolve(hex);
        } else {
          resolve(null);
        }
      } catch (e) {
        console.error("Error extracting image color:", e);
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = imageSrc;
  });
};

export default function CataCraftDesigner({ activeDesign, onBackToList }) {
  // Uploaded/parsed data
  const [fileName, setFileName] = useState(null);
  const [categoryRows, setCategoryRows] = useState([]);
  const [productRows, setProductRows] = useState([]);
  const [groupedProducts, setGroupedProducts] = useState([]); // New state for grouped products
  const [globalDiscount, setGlobalDiscount] = useState("");
  const [excelFilePath, setExcelFilePath] = useState("");

  // UI state
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState(null);

  // Page Image Export modal & selection state
  const [showPageExportModal, setShowPageExportModal] = useState(false);
  const [selectedPages, setSelectedPages] = useState([]);
  const [totalPagesCount, setTotalPagesCount] = useState(1);
  const [isExportingImages, setIsExportingImages] = useState(false);

  // Company / PDF theme settings
  const [company, setCompany] = useState({
    name: "",
    tagline: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    phone: "",
    whatsapp: "",
    email: ""
  });
  const [bgImageFile, setBgImageFile] = useState(null);
  const [bgImageUrl, setBgImageUrl] = useState(null);
  const [serverBgFilePath, setServerBgFilePath] = useState("");

  // Custom Edited Page Image Attachment state
  const [customPageConfig, setCustomPageConfig] = useState({
    imageSrc: "",
    file: null,
    mode: "append_end", // "append_end", "replace_last", "replace_num"
    pageNum: 1,
  });

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showColorPickerPageBackground, setShowColorPickerPageBackground] = useState(false);
  const [showColorPickerheader, setShowColorPickerheader] = useState(false);
  const [showColorPickerheaderback, setShowColorPickerheaderback] = useState(false);
  const [showColorPickercategorytext, setShowColorPickercategorytext] = useState(false);
  const [showColorPickerfootertext, setShowColorPickerfootertext] = useState(false);
  const [showColorPickerfooterbackground, setShowColorPickerfooterbackground] = useState(false);
  const [showColorPickerTableBackground, setShowColorPickerTableBackground] = useState(false);
  const [showColorPickerbordercolor, setShowColorPickerbordercolor] = useState(false);

  const [customPrimary, setCustomPrimary] = useState("#000000");
  const [customSecondary, setCustomSecondary] = useState("#FFD54F");

  const defaultTheme = {
    pageMargin: 10,
    fontSize: 10,
    headerFontSize: 16,
    categoryBg: "#000000",
    categoryText: "#FFD54F",
    headertext: "#FFFFFF",
    headerbackground: "#000000",
    categoryFontSize: 12,
    pageBackground: "white",
    bgOpacity: 1,
    productsPerPage: 38,
    firstPageProducts: 30,
    footerBg: "#000000",
    footerText: "#FFD54F",
    borderRadius: 1,
    tableBg: "#ffffff",
    altTableBg: "#FFFDF0",
    bordercolor: "#000000",
    hideDiscountRate: false,
    hideRsPs: false,
    showRequirement: false,
    targetPages: null,
    enableAltRow: false,
    enableTheme1Header: true,
    enableTheme2: false,
    extractedBgColor: "",
    hideFooter: false,
    showCategoryDiscountText: true,
    enableSeparateTamilColumn: false,
    pricelistTitle: "// PRICELIST - 2026 //",
    primaryColor: "#000000",
    secondaryColor: "#FFD54F",
    selectedPresetId: "black_gold",
  };

  const [theme, setTheme] = useState(defaultTheme);

  const applyDualColorTheme = (preset) => {
    setCustomPrimary(preset.primary);
    setCustomSecondary(preset.secondary);
    setTheme((prev) => ({
      ...prev,
      categoryBg: preset.categoryBg,
      categoryText: preset.categoryText,
      headerbackground: preset.headerBg,
      headertext: preset.headerText,
      footerBg: preset.footerBg,
      footerText: preset.footerText,
      bordercolor: preset.borderColor,
      tableBg: preset.tableBg || "#ffffff",
      altTableBg: preset.altTableBg || "#ffffff",
      primaryColor: preset.primary,
      secondaryColor: preset.secondary,
      selectedPresetId: preset.id,
    }));
  };

  const applyCustomDualColors = (primary, secondary) => {
    setCustomPrimary(primary);
    setCustomSecondary(secondary);
    setTheme((prev) => ({
      ...prev,
      categoryBg: primary,
      categoryText: secondary,
      headerbackground: primary,
      headertext: "#ffffff",
      footerBg: primary,
      footerText: secondary,
      bordercolor: primary,
      primaryColor: primary,
      secondaryColor: secondary,
      selectedPresetId: "custom",
    }));
  };

  const isValidHex = (hex) => /^#([0-9A-F]{3}){1,2}$/i.test(hex);

  // Handle color changes
  const handleColorChangePageBg = (color) => {
    setTheme((prev) => ({
      ...prev,
      pageBackground: color.hex,
      extractedBgColor: color.hex,
      ...(prev.enableTheme2 ? { tableBg: color.hex } : {}),
    }));
  };
  const handleColorChangeCategoryBg = (color) => {
    setTheme((prev) => ({ ...prev, categoryBg: color.hex }));
  };
  const handleColorChangeheadertext = (color) => {
    setTheme({ ...theme, headertext: color.hex });
  };
  const handleColorChangeheaderbackground = (color) => {
    setTheme({ ...theme, headerbackground: color.hex });
  };
  const handleColorChangecategorytext = (color) => {
    setTheme({ ...theme, categoryText: color.hex });
  };
  const handleColorChangefootertext = (color) => {
    setTheme({ ...theme, footerText: color.hex });
  };
  const handleColorChangefooterbackground = (color) => {
    setTheme({ ...theme, footerBg: color.hex });
  };
  const handleColorChangeTableBackground = (color) => {
    setTheme({ ...theme, tableBg: color.hex });
  };
  const handleColorChangebordercolor = (color) => {
    setTheme({ ...theme, bordercolor: color.hex });
  };

  // Helper to calculate total weighted items count across categories, products, and groups
  const calculateTotalWeightedCount = () => {
    let totalWeight = 0;
    (categoryRows || []).forEach((cat) => {
      const catProducts = (productRows || []).filter((p) => p.category_name === cat.category_name);
      if (catProducts.length > 0) {
        totalWeight += 1.2; // Category header takes 1.2 weight units
        totalWeight += catProducts.length * 1.0; // Product rows take 1.0 weight unit
      }
    });

    (groupedProducts || []).forEach((group) => {
      totalWeight += 1.2;
      totalWeight += (group.items || []).length * 1.0;
    });

    return totalWeight;
  };

  // Auto-fit products into target page count filling pages to bottom end
  const autoFitToTargetPages = (targetPagesCount) => {
    if (!targetPagesCount || targetPagesCount < 1) return;

    const totalWeight = calculateTotalWeightedCount();
    if (totalWeight === 0) return;

    const hasHeader = Boolean(theme.enableTheme1Header);
    const hideFooter = Boolean(theme.hideFooter || hasHeader);

    // Maximum safe physical page capacities when footer is hidden (allowing pages to fill all the way to the bottom end)
    const maxFirstCap = hasHeader ? (hideFooter ? 46 : 34) : (hideFooter ? 54 : 42);
    const maxNormalCap = hideFooter ? 54 : 42;

    let newProductsPerPage = maxNormalCap;
    let newFirstPageProducts = maxFirstCap;

    if (targetPagesCount === 1) {
      newFirstPageProducts = Math.ceil(totalWeight);
      newProductsPerPage = Math.ceil(totalWeight);
    } else {
      // Pack Page 1, Page 2, Page 3 100% to the max bottom line capacity
      newProductsPerPage = maxNormalCap;
      newFirstPageProducts = maxFirstCap;
    }

    setTheme((prev) => ({
      ...prev,
      targetPages: targetPagesCount,
      firstPageProducts: newFirstPageProducts,
      productsPerPage: newProductsPerPage,
    }));
  };

  useEffect(() => {
    if (theme.targetPages && theme.targetPages > 0) {
      autoFitToTargetPages(theme.targetPages);
    }
  }, [categoryRows.length, productRows.length, groupedProducts?.length, theme.enableTheme1Header]);

  // Reset theme to default
  const resetTheme = () => {
    setCustomPrimary("#000000");
    setCustomSecondary("#FFD54F");
    setTheme(defaultTheme);
  };

  function fixEncoding(str) {
    try {
      return decodeURIComponent(escape(str));
    } catch {
      return str;
    }
  }



  // Helper function to process Excel ArrayBuffer data into component state
  const processExcelBuffer = (arrayBuffer, savedCategoryDiscounts = null) => {
    try {
      const data = new Uint8Array(arrayBuffer);
      const wb = XLSX.read(data, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      const catMap = new Map();
      const products = [];
      const grouped = [];
      rows.forEach((row) => {
        const isGroupRow =
          row.group_name && row.group_price && row.group_product_name && row.group_per_unit;

        if (isGroupRow) {
          const groupName = row.group_name;
          let existingGroup = grouped.find((g) => g.group_name === groupName);

          if (existingGroup) {
            existingGroup.items.push({
              s_no: existingGroup.items.length + 1,
              product_name: fixEncoding(row.group_product_name),
              per_unit: row.group_per_unit,
            });
          } else {
            grouped.push({
              group_name: groupName,
              group_price: row.group_price,
              items: [
                {
                  s_no: 1,
                  product_name: fixEncoding(row.group_product_name),
                  per_unit: row.group_per_unit,
                },
              ],
            });
          }
        } else {
          const cname = row.category_name || row.category || "Uncategorized";
          if (!catMap.has(cname)) {
            let initialDiscount = row.discount || 0;
            let discountToggle = true;

            // Apply saved category discount override if present
            if (savedCategoryDiscounts && Array.isArray(savedCategoryDiscounts)) {
              const match = savedCategoryDiscounts.find((sc) => sc.category_name === cname);
              if (match) {
                if (match.discount !== undefined) initialDiscount = match.discount;
                if (match.discountToggle !== undefined) discountToggle = match.discountToggle;
              }
            }

            catMap.set(cname, {
              category_name: cname,
              discount: initialDiscount,
              discountToggle: discountToggle,
            });
          }

          products.push({
            category_name: cname,
            product_name_en:
              row.product_name_en || row.product_name || row.title || "",
            product_name_ta: fixEncoding(row.product_name_ta || ""),
            net_rate: row.net_rate || row.price || 0,
            per_unit: row.per_unit || row.content || "",
          });
        }
      });

      const categoriesArr = Array.from(catMap.values());
      setCategoryRows(categoriesArr);
      setProductRows(products);
      setGroupedProducts(grouped);
      if (categoriesArr.length > 0) {
        setSelectedCategory(categoriesArr[0].category_name);
      }
      return { products, categories: categoriesArr };
    } catch (err) {
      console.error("Error processing Excel buffer:", err);
      return null;
    }
  };

  // Initialize from activeDesign if provided
  useEffect(() => {
    if (activeDesign) {
      setCompany({
        name: activeDesign.company_name || "",
        tagline: activeDesign.tagline || "",
        addressLine1: activeDesign.address_line1 || "",
        addressLine2: activeDesign.address_line2 || "",
        addressLine3: activeDesign.address_line3 || "",
        phone: activeDesign.phone || "",
        whatsapp: activeDesign.whatsapp || "",
        email: activeDesign.email || "",
      });

      if (activeDesign.bg_image_path) {
        setServerBgFilePath(activeDesign.bg_image_path);
        fetchCraftDesignExcelApi(activeDesign.bg_image_path)
          .then((buf) => {
            const blob = new Blob([buf], { type: "image/jpeg" });
            const localBlobUrl = URL.createObjectURL(blob);
            setBgImageUrl(localBlobUrl);
          })
          .catch((err) => {
            console.error("Failed to load background image:", err);
            const fallbackUrl = activeDesign.bg_image_path.startsWith("http") || activeDesign.bg_image_path.startsWith("data:")
              ? activeDesign.bg_image_path
              : `${SERVER_BASE_URL}/${activeDesign.bg_image_path}`;
            setBgImageUrl(fallbackUrl);
          });
      }

      let savedCategoryDiscounts = null;

      if (activeDesign.theme_config) {
        try {
          const parsedTheme = typeof activeDesign.theme_config === "string" ? JSON.parse(activeDesign.theme_config) : activeDesign.theme_config;
          if (parsedTheme && typeof parsedTheme === "object") {
            setTheme((prev) => ({ ...prev, ...parsedTheme }));
            if (parsedTheme.company) {
              setCompany((prev) => ({
                ...prev,
                ...parsedTheme.company,
                logo: parsedTheme.company.logo || "",
                rightImage: parsedTheme.company.rightImage || "",
              }));
            }
            if (parsedTheme.categoryDiscounts) {
              savedCategoryDiscounts = parsedTheme.categoryDiscounts;
            }
            if (parsedTheme.customPageConfig) {
              setCustomPageConfig((prev) => ({ ...prev, ...parsedTheme.customPageConfig }));
            }
          }
        } catch (e) {
          console.error("Error parsing theme config:", e);
        }
      }

      if (activeDesign.excel_file_path) {
        setExcelFilePath(activeDesign.excel_file_path);

        // Fetch and parse the saved Excel file from server for PDF preview
        fetchCraftDesignExcelApi(activeDesign.excel_file_path)
          .then((arrayBuffer) => {
            const fileNameOnly = activeDesign.excel_file_path.split("/").pop();
            setFileName(fileNameOnly);
            processExcelBuffer(arrayBuffer, savedCategoryDiscounts);
            NotifyData("Loaded saved Excel products & design configuration!", "success");
          })
          .catch((err) => {
            console.error("Failed to fetch saved Excel file:", err);
            NotifyData("Failed to load saved Excel products from server", "warning");
          });
      }
    }
  }, [activeDesign]);

  // Client-side canvas image compression function (Converts MB files into lightweight KB JPEGs with rich vivid colors)
  const compressImageClient = (file, maxWidth = 1200, quality = 0.70) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, ".jpg"), {
                type: "image/jpeg",
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            },
            "image/jpeg",
            quality
          );
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Background image upload with auto-compression & multipart upload
  const handleBgUpload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;

    try {
      // 1. Client side image compression into lightweight KB
      const compressedFile = await compressImageClient(f, 1000, 0.50);
      setBgImageFile(compressedFile);

      // Read as Data URL for instant, 100% reliable local preview in React PDF
      const reader = new FileReader();
      reader.onload = async (evt) => {
        const previewDataUrl = evt.target.result;
        setBgImageUrl(previewDataUrl);

        // Auto-extract color from uploaded background image
        try {
          const extractedColor = await extractImageColor(previewDataUrl);
          if (extractedColor) {
            setTheme((prev) => ({
              ...prev,
              extractedBgColor: extractedColor,
              ...(prev.enableTheme2
                ? {
                    pageBackground: extractedColor,
                    tableBg: extractedColor,
                  }
                : {}),
            }));
          }
        } catch (colorErr) {
          console.warn("Failed to extract color from image:", colorErr);
        }

        // 2. Server side multipart upload & GD compression
        try {
          const uploadRes = await uploadCraftDesignFileApi(compressedFile, "image");
          if (uploadRes?.filepath) {
            setServerBgFilePath(uploadRes.filepath);
            NotifyData(`Image uploaded & compressed (${uploadRes.size_kb} KB)`, "success");
          }
        } catch (uploadErr) {
          console.error("Server upload failed, using local preview:", uploadErr);
        }
      };
      reader.readAsDataURL(compressedFile);
    } catch (err) {
      console.error("Image upload failed:", err);
      // Fallback
      setBgImageFile(f);
      setBgImageUrl(URL.createObjectURL(f));
    }
  };

  // Remove Background Image
  const handleRemoveBgImage = () => {
    setBgImageFile(null);
    setBgImageUrl("");
    setServerBgFilePath("");
    NotifyData("Background image removed completely", "info");
  };

  // Left Logo Upload
  const handleLogoUpload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    try {
      const compressedFile = await compressImageClient(f, 400, 0.85);
      const reader = new FileReader();
      reader.onload = async (evt) => {
        const previewDataUrl = evt.target.result;
        setCompany((prev) => ({ ...prev, logo: previewDataUrl }));

        try {
          const uploadRes = await uploadCraftDesignFileApi(compressedFile, "image");
          if (uploadRes?.filepath) {
            const serverUrl = `${SERVER_BASE_URL}/${uploadRes.filepath}`;
            setCompany((prev) => ({ ...prev, logo: serverUrl }));
          }
        } catch (uploadErr) {
          console.warn("Server upload failed for logo, using preview data URL:", uploadErr);
        }
        NotifyData("Header Logo uploaded & saved!", "success");
      };
      reader.readAsDataURL(compressedFile);
    } catch (err) {
      console.error("Logo upload error:", err);
      const reader = new FileReader();
      reader.onload = (evt) => {
        setCompany((prev) => ({ ...prev, logo: evt.target.result }));
      };
      reader.readAsDataURL(f);
    }
  };

  const handleRemoveLogo = () => {
    setCompany((prev) => ({
      ...prev,
      logo: "",
    }));
  };

  // Right Illustration Image Upload
  const handleRightImageUpload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    try {
      const compressedFile = await compressImageClient(f, 400, 0.85);
      const reader = new FileReader();
      reader.onload = async (evt) => {
        const previewDataUrl = evt.target.result;
        setCompany((prev) => ({ ...prev, rightImage: previewDataUrl }));

        try {
          const uploadRes = await uploadCraftDesignFileApi(compressedFile, "image");
          if (uploadRes?.filepath) {
            const serverUrl = `${SERVER_BASE_URL}/${uploadRes.filepath}`;
            setCompany((prev) => ({ ...prev, rightImage: serverUrl }));
          }
        } catch (uploadErr) {
          console.warn("Server upload failed for right image, using preview data URL:", uploadErr);
        }
        NotifyData("Header Right Side Image uploaded & saved!", "success");
      };
      reader.readAsDataURL(compressedFile);
    } catch (err) {
      console.error("Right image upload error:", err);
      const reader = new FileReader();
      reader.onload = (evt) => {
        setCompany((prev) => ({ ...prev, rightImage: evt.target.result }));
      };
      reader.readAsDataURL(f);
    }
  };

  const handleRemoveRightImage = () => {
    setCompany((prev) => ({
      ...prev,
      rightImage: "",
    }));
  };

  // Handle custom edited page image upload
  const handleCustomPageImageUpload = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      setCustomPageConfig((prev) => ({
        ...prev,
        imageSrc: evt.target.result,
        file: f,
      }));
      NotifyData("Custom edited page image attached!", "success");
    };
    reader.readAsDataURL(f);
  };

  const handleRemoveCustomPageImage = () => {
    setCustomPageConfig((prev) => ({
      ...prev,
      imageSrc: "",
      file: null,
    }));
    NotifyData("Custom page image removed", "info");
  };

  // Excel file upload & auto upload to backend
  const handleFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFileName(f.name);

    // Read excel locally for immediate preview
    const reader = new FileReader();
    reader.onload = (evt) => {
      processExcelBuffer(evt.target.result);
    };
    reader.readAsArrayBuffer(f);

    // Upload excel to PHP backend
    try {
      const uploadRes = await uploadCraftDesignFileApi(f, "excel");
      if (uploadRes?.filepath) {
        setExcelFilePath(uploadRes.filepath);
        NotifyData("Product Excel uploaded successfully", "success");
      }
    } catch (err) {
      console.error("Excel upload failed:", err);
    }
  };

  // Download Product Excel File
  const handleDownloadProductExcel = () => {
    if (excelFilePath) {
      const downloadUrl = `${SERVER_BASE_URL}/${excelFilePath}`;
      window.open(downloadUrl, "_blank");
    } else if (productRows.length > 0) {
      // Export current loaded product rows to Excel
      const ws = XLSX.utils.json_to_sheet(productRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Products");
      XLSX.writeFile(wb, `${company.name || "catacraft"}_products.xlsx`);
    } else {
      NotifyData("No product excel file uploaded yet", "info");
    }
  };

  // Update category discount
  const setCategoryDiscount = (name, newDiscount) => {
    setCategoryRows((prev) => (prev || []).map((c) => (c.category_name === name ? { ...c, discount: Number(newDiscount) } : c)));
  };

  // Toggle discount on/off for a category
  const toggleCategoryDiscount = (name) => {
    setCategoryRows((prev) =>
      (prev || []).map((c) =>
        c.category_name === name ? { ...c, discountToggle: !c.discountToggle } : c
      )
    );
  };

  // Toggle discount ON or OFF globally for all categories at once
  const toggleAllCategoryDiscounts = (enableState) => {
    setCategoryRows((prev) =>
      (prev || []).map((c) => ({
        ...c,
        discountToggle: enableState,
      }))
    );
    NotifyData(
      enableState
        ? "All category discounts enabled (ON)"
        : "All category discounts locked & hidden (OFF)",
      "info"
    );
  };

  // Save Craft Design Configuration to PHP MySQL Database
  const handleSubmit = async () => {
    setIsUploading(true);
    setResult(null);
    try {
      const themeToSave = {
        ...theme,
        company: {
          name: company.name || "",
          tagline: company.tagline || "",
          addressLine1: company.addressLine1 || "",
          addressLine2: company.addressLine2 || "",
          addressLine3: company.addressLine3 || "",
          phone: company.phone || "",
          whatsapp: company.whatsapp || "",
          email: company.email || "",
          logo: company.logo || "",
          rightImage: company.rightImage || "",
        },
        categoryDiscounts: (categoryRows || []).map((c) => ({
          category_name: c.category_name,
          discount: c.discount,
          discountToggle: c.discountToggle,
        })),
        customPageConfig: {
          imageSrc: customPageConfig.imageSrc,
          mode: customPageConfig.mode,
          pageNum: customPageConfig.pageNum,
        },
      };

      const payload = {
        company_name: company.name || "Untitled Craft Design",
        tagline: company.tagline || "",
        address_line1: company.addressLine1 || "",
        address_line2: company.addressLine2 || "",
        address_line3: company.addressLine3 || "",
        phone: company.phone || "",
        whatsapp: company.whatsapp || "",
        email: company.email || "",
        bg_image_path: bgImageUrl ? (serverBgFilePath || bgImageUrl) : "",
        excel_file_path: excelFilePath || "",
        theme_config: JSON.stringify(themeToSave),
      };

      let res;
      if (activeDesign && activeDesign.id) {
        res = await updateCraftDesignApi({ id: activeDesign.id, ...payload });
        NotifyData("Craft Design configuration updated successfully!", "success");
      } else {
        res = await addCraftDesignApi(payload);
        NotifyData("Craft Design configuration saved successfully!", "success");
      }

      setResult({ ok: true, data: res });
    } catch (err) {
      setResult({ ok: false, error: err.message });
      NotifyData("Failed to save configuration: " + (err.message || "Unknown error"), "error");
    } finally {
      setIsUploading(false);
    }
  };

  // Apply global discount to all categories
  const applyGlobalDiscount = (value) => {
    setGlobalDiscount(value);
    setCategoryRows((prev) =>
      (prev || []).map((c) => ({
        ...c,
        discount: value,
        discountToggle: true, // Reset to "on" when applying global discount
      }))
    );
  };

  // Helper to ensure image URLs are converted to clean Blob URLs for XObject caching in React-PDF
  const preparePdfBgImage = async (imageSrc) => {
    if (!imageSrc) return null;
    if (typeof imageSrc === 'string' && imageSrc.startsWith('data:')) {
      try {
        const res = await fetch(imageSrc);
        const blob = await res.blob();
        return URL.createObjectURL(blob);
      } catch (e) {
        return imageSrc;
      }
    }
    return imageSrc;
  };

  // Download generated PDF
  const handleDownloadPdf = async () => {
    try {
      const cleanBgSrc = await preparePdfBgImage(bgImageUrl);
      const doc = (
        <BillFour
          products={productRows}
          categoryRows={categoryRows}
          categoryName={"All Products"}
          categoryDiscount={0}
          company={company}
          bgImageSrc={cleanBgSrc}
          theme={theme}
          groupedProducts={groupedProducts}
          customPageConfig={customPageConfig}
        />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(company.name || "catacraft").replace(/\s+/g, '_')}_design.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download error:", err);
      NotifyData("Failed to generate PDF: " + (err?.message || "Unknown error"), "error");
    }
  };

  // Helper to generate PDF Blob
  const generatePdfBlob = async () => {
    const cleanBgSrc = await preparePdfBgImage(bgImageUrl);
    const doc = (
      <BillFour
        products={productRows ?? []}
        categoryRows={categoryRows}
        categoryName={selectedCategory || "All Products"}
        categoryDiscount={categoryRows.find(c => c.category_name === selectedCategory)?.discount || 0}
        company={company}
        bgImageSrc={cleanBgSrc}
        theme={theme}
        groupedProducts={groupedProducts}
        customPageConfig={customPageConfig}
      />
    );
    return await pdf(doc).toBlob();
  };

  // Helper: Convert PDF Blob to High-Res Image PNGs for selected page indices (1-indexed)
  const convertPdfBlobToPageImages = async (pdfBlob, pageNumbersToExport = []) => {
    const arrayBuffer = await pdfBlob.arrayBuffer();

    let pdfjs = window.pdfjsLib;
    if (!pdfjs) {
      await new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js";
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
      pdfjs = window.pdfjsLib;
      pdfjs.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";
    }

    const pdfDoc = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    const numPages = pdfDoc.numPages;
    const exportedImages = [];

    const pagesToProcess = pageNumbersToExport.length > 0
      ? pageNumbersToExport.filter(p => p >= 1 && p <= numPages)
      : [numPages]; // Default to last page if empty

    for (const pageNum of pagesToProcess) {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale: 2.5 }); // 2.5x high resolution crisp render

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;

      const dataUrl = canvas.toDataURL("image/png", 1.0);
      exportedImages.push({ pageNum, dataUrl });
    }

    return exportedImages;
  };

  // 1-Click Export Last Page Only as PNG Image
  const handleExportLastPageImage = async () => {
    try {
      setIsExportingImages(true);
      NotifyData("Rendering Last Page as PNG Image...", "info");
      const pdfBlob = await generatePdfBlob();
      const images = await convertPdfBlobToPageImages(pdfBlob, []);

      if (images && images.length > 0) {
        images.forEach((img) => {
          const a = document.createElement("a");
          a.href = img.dataUrl;
          a.download = `${(company.name || "catacraft").replace(/\s+/g, '_')}_page_${img.pageNum}_last.png`;
          a.click();
        });
        NotifyData("Last Page image exported successfully!", "success");
      }
    } catch (err) {
      console.error("Export Last Page failed:", err);
      NotifyData("Failed to export last page image", "error");
    } finally {
      setIsExportingImages(false);
    }
  };

  // Open Modal to select multi pages
  const handleOpenPageExportModal = async () => {
    try {
      NotifyData("Preparing page list preview...", "info");
      const pdfBlob = await generatePdfBlob();
      const arrayBuffer = await pdfBlob.arrayBuffer();

      let pdfjs = window.pdfjsLib;
      if (!pdfjs) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js";
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
        pdfjs = window.pdfjsLib;
        pdfjs.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";
      }

      const pdfDoc = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      const count = pdfDoc.numPages;
      setTotalPagesCount(count);
      setSelectedPages([count]); // Default to last page selected
      setShowPageExportModal(true);
    } catch (err) {
      console.error("Failed to prepare page export modal:", err);
      NotifyData("Error loading page list", "error");
    }
  };

  // Download Selected Pages as Images
  const handleDownloadSelectedPages = async () => {
    if (selectedPages.length === 0) {
      NotifyData("Please select at least one page to export", "warning");
      return;
    }
    try {
      setIsExportingImages(true);
      NotifyData(`Exporting ${selectedPages.length} selected page image(s)...`, "info");
      const pdfBlob = await generatePdfBlob();
      const images = await convertPdfBlobToPageImages(pdfBlob, selectedPages);

      images.forEach((img) => {
        const a = document.createElement("a");
        a.href = img.dataUrl;
        a.download = `${(company.name || "catacraft").replace(/\s+/g, '_')}_page_${img.pageNum}.png`;
        a.click();
      });
      NotifyData("Selected page images exported successfully!", "success");
      setShowPageExportModal(false);
    } catch (err) {
      console.error("Export selected pages failed:", err);
      NotifyData("Failed to export selected page images", "error");
    } finally {
      setIsExportingImages(false);
    }
  };


  // Layout styles
  const layoutStyle = {
    display: "grid",
    gridTemplateColumns: "420px 1fr",
    gap: 16,
    height: "90vh",
    padding: 12,
  };



  // Download Sample Excel (.xlsx) Template
  const handleDownloadSampleExcel = () => {
    const sampleData = [
      {
        category_name: "SPARKLERS",
        product_name_en: "7 cm Electric Sparklers",
        product_name_ta: "7 செ.மீ எலக்ட்ரிக் சங்கு",
        net_rate: 15,
        per_unit: "1 Box",
        discount: 10,
        group_name: "",
        group_price: "",
        group_product_name: "",
        group_per_unit: ""
      },
      {
        category_name: "SPARKLERS",
        product_name_en: "10 cm Crackling Sparklers",
        product_name_ta: "10 செ.மீ கிராக்ளிங் சங்கு",
        net_rate: 35,
        per_unit: "1 Box",
        discount: 10,
        group_name: "",
        group_price: "",
        group_product_name: "",
        group_per_unit: ""
      },
      {
        category_name: "FLOWER POTS",
        product_name_en: "Flower Pot Big",
        product_name_ta: "பூச்சட்டி பெரியது",
        net_rate: 120,
        per_unit: "1 Box",
        discount: 15,
        group_name: "",
        group_price: "",
        group_product_name: "",
        group_per_unit: ""
      },
      {
        category_name: "FLOWER POTS",
        product_name_en: "Flower Pot Special",
        product_name_ta: "பூச்சட்டி ஸ்பெஷல்",
        net_rate: 180,
        per_unit: "1 Box",
        discount: 15,
        group_name: "",
        group_price: "",
        group_product_name: "",
        group_per_unit: ""
      },
      {
        category_name: "",
        product_name_en: "",
        product_name_ta: "",
        net_rate: "",
        per_unit: "",
        discount: "",
        group_name: "FAMILY COMBO PACK",
        group_price: 2500,
        group_product_name: "10 Cm Electric Sparklers",
        group_per_unit: "1 Box"
      },
      {
        category_name: "",
        product_name_en: "",
        product_name_ta: "",
        net_rate: "",
        per_unit: "",
        discount: "",
        group_name: "FAMILY COMBO PACK",
        group_price: 2500,
        group_product_name: "Flower Pot Big",
        group_per_unit: "1 Box"
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    worksheet["!cols"] = [
      { wch: 18 },
      { wch: 28 },
      { wch: 28 },
      { wch: 12 },
      { wch: 12 },
      { wch: 10 },
      { wch: 22 },
      { wch: 14 },
      { wch: 26 },
      { wch: 16 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample_Catalog");
    XLSX.writeFile(workbook, "CataCraft_Sample_Format.xlsx");
  };

  return (
    <div id="main">
      <div style={{ fontFamily: "Inter, sans-serif" }} className="p-3">
        <div className="min-h-screen bg-gray-100 p-2">
          <div className="bg-white shadow rounded p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                {onBackToList && (
                  <Button variant="outline-secondary" size="sm" onClick={onBackToList}>
                    ← Back to Listing
                  </Button>
                )}
                <h5 className="mb-0 fw-bold text-primary">
                  Craft Designer Workspace {company.name ? `- ${company.name}` : ''}
                </h5>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={handleDownloadProductExcel}
                  title="Download Product Excel File"
                >
                  📊 Product Excel Download
                </Button>
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={handleExportLastPageImage}
                  disabled={isExportingImages}
                  title="Export Last Page as High-Res PNG Image (Instant 1-Click)"
                >
                  🖼️ Export Last Page Image
                </Button>
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={handleOpenPageExportModal}
                  disabled={isExportingImages}
                  title="Multi-Select PDF Pages to Export as High-Res Images"
                >
                  🖼️ Select Pages to Export Image
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleDownloadPdf}
                  title="Export as PDF"
                >
                  📄 Export PDF
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSubmit}
                  disabled={isUploading}
                >
                  💾 {isUploading ? 'Saving...' : 'Save Configuration'}
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded p-2">
            <div style={layoutStyle}>
              {/* LEFT: Controls & Inputs */}
              <div className="overflow-auto p-3 rounded" style={{ border: "1px solid #e5e7eb" }}>

                <section className="mb-4 p-3 bg-light rounded border">
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <label className="fw-bold mb-0 text-dark">
                      Upload Excel (categories, products, grouped products)
                    </label>
                    <button
                      type="button"
                      className="btn btn-success btn-sm d-flex align-items-center gap-1 shadow-sm px-3"
                      onClick={handleDownloadSampleExcel}
                      title="Download Sample Excel Format Template (.xlsx)"
                    >
                      📥 <span>Download Sample Excel</span>
                    </button>
                  </div>

                  <div className="mt-2">
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFile}
                      className="form-control form-control-sm"
                    />
                  </div>

                  {fileName && (
                    <div className="mt-2 text-success small fw-medium">
                      ✓ File Loaded: <strong>{fileName}</strong>
                    </div>
                  )}

                  <div className="mt-3 p-2 bg-white rounded border small">
                    <div className="d-flex justify-content-between text-muted border-bottom pb-1 mb-1">
                      <span>Categories detected:</span>
                      <strong className="text-dark">{categoryRows.length}</strong>
                    </div>
                    <div className="d-flex justify-content-between text-muted border-bottom pb-1 mb-1">
                      <span>Products detected:</span>
                      <strong className="text-dark">{productRows.length}</strong>
                    </div>
                    <div className="d-flex justify-content-between text-muted">
                      <span>Grouped Products detected:</span>
                      <strong className="text-dark">{groupedProducts.length}</strong>
                    </div>
                  </div>

                  <div className="mt-2 text-muted small" style={{ fontSize: '11px' }}>
                    ℹ️ <strong>Format Headers:</strong> category_name, product_name_en, product_name_ta, net_rate, per_unit, discount, group_name, group_price, group_product_name, group_per_unit
                  </div>
                </section>

                <section className="mb-6 p-3 bg-white rounded border shadow-sm">
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <label className="block text-sm font-semibold text-gray-800 mb-0">
                      🏷️ Category Discount Settings
                    </label>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-success"
                        size="sm"
                        className="py-1 px-2 text-xs"
                        onClick={() => toggleAllCategoryDiscounts(true)}
                        title="Enable discount display & calculations for ALL categories"
                      >
                        🔓 Turn All ON
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="py-1 px-2 text-xs"
                        onClick={() => toggleAllCategoryDiscounts(false)}
                        title="Hide discount percentages & discount amounts for ALL categories"
                      >
                        🔒 Turn All OFF
                      </Button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="text-xs text-muted mb-1 block">Set Global Discount (%) for All Categories:</label>
                    <div className="d-flex gap-2">
                      <input
                        type="number"
                        value={globalDiscount || 0}
                        onChange={(e) => applyGlobalDiscount(e.target.value)}
                        placeholder="Enter discount %"
                        className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2" style={{ maxHeight: "280px", overflowY: "auto" }}>
                    {(categoryRows || []).map((c, idx) => (
                      <div
                        key={idx}
                        className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm hover:bg-white transition"
                      >
                        {/* Category Name */}
                        <div className="flex-1 text-gray-800 font-medium truncate me-2">
                          {c.category_name}
                        </div>

                        {/* Discount Input */}
                        <input
                          type="number"
                          value={c.discount || 0}
                          className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onChange={(e) => {
                            const val = e.target.value === "" ? 0 : Number(e.target.value);
                            setCategoryDiscount(c.category_name, val);
                          }}
                        />

                        {/* Toggle Button */}
                        <button
                          className={`ml-3 toggle-btn ${c.discountToggle ? "on" : ""}`}
                          onClick={() => toggleCategoryDiscount(c.category_name)}
                          title={c.discountToggle ? "Discount Active (ON)" : "Discount Locked & Hidden (OFF)"}
                        >
                          <span className="knob"></span>
                          <span className="label">{c.discountToggle ? "ON" : "OFF"}</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </section>



                <section className="mb-3">
                  <label className="block font-medium">Grouped Products</label>
                  <div className="mt-2">
                    {(groupedProducts || []).map((group, idx) => (
                      <div key={idx} className="mb-3">
                        <div className="font-medium">{group.group_name} - {group.group_price} Rs</div>
                        <table className="table-auto w-full mt-1 border">
                          <thead>
                            <tr>
                              <th className="border px-2 py-1">S.No</th>
                              <th className="border px-2 py-1">Product Name</th>
                              <th className="border px-2 py-1">Per</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(group.items || []).map((item, i) => (
                              <tr key={i}>
                                <td className="border px-2 py-1">{item.s_no}</td>
                                <td className="border px-2 py-1">{item.product_name}</td>
                                <td className="border px-2 py-1">{item.per_unit}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="mb-3">
                  <label className="block font-medium mb-3">Company details</label>
                  <div className="entryarea">
                    <input
                      type="text"
                      value={company.name}
                      className="input"
                      placeholder=""
                      onChange={(e) => setCompany({ ...company, name: e.target.value })}
                    />
                    <div className="labelline">Enter company name</div>
                  </div>
                  <div className="entryarea">
                    <input
                      type="text"
                      placeholder=""
                      className="input"
                      value={company.tagline}
                      onChange={(e) => setCompany({ ...company, tagline: e.target.value })}
                    />
                    <div className="labelline">Enter Tagline</div>
                  </div>
                  <div className="entryarea">
                    <input
                      placeholder=""
                      className="input"
                      value={company.addressLine1}
                      onChange={(e) => setCompany({ ...company, addressLine1: e.target.value })}
                    />
                    <div className="labelline">Enter Address line1</div>
                  </div>
                  <div className="entryarea">
                    <input
                      placeholder=""
                      className="input"
                      value={company.addressLine2}
                      onChange={(e) => setCompany({ ...company, addressLine2: e.target.value })}
                    />
                    <div className="labelline">Enter Address line2</div>
                  </div>
                  <div className="entryarea">
                    <input
                      placeholder=""
                      className="input"
                      value={company.addressLine3}
                      onChange={(e) => setCompany({ ...company, addressLine3: e.target.value })}
                    />
                    <div className="labelline">Enter Address line3</div>
                  </div>
                  <div className="entryarea">
                    <input
                      placeholder=""
                      className="input"
                      value={company.phone}
                      onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                    />
                    <div className="labelline">Enter Phone.no</div>
                  </div>
                  <div className="entryarea">
                    <input
                      placeholder=""
                      className="input"
                      value={company.whatsapp}
                      onChange={(e) => setCompany({ ...company, whatsapp: e.target.value })}
                    />
                    <div className="labelline">Enter Whatsapp.no</div>
                  </div>
                  <div className="entryarea">
                    <input
                      placeholder=""
                      className="input"
                      value={company.email}
                      onChange={(e) => setCompany({ ...company, email: e.target.value })}
                    />
                    <div className="labelline">Enter email</div>
                  </div>
                </section>

                {/* Header Logo & Right Illustration Upload Section */}
                <section className="mb-4 p-3 bg-white rounded border shadow-sm">
                  <h6 className="fw-bold mb-3 text-dark d-flex align-items-center gap-1">
                    🖼️ Header Images & Logos (Theme 1 Header)
                  </h6>

                  {/* Left Logo Upload */}
                  <div className="mb-3 p-2 bg-light rounded border">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label small fw-bold mb-0 text-dark">
                        Header Left Logo (Brand Emblem)
                      </label>
                      {company.logo && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={handleRemoveLogo}
                          className="py-0 px-2 small"
                        >
                          🗑️ Remove Logo
                        </Button>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="form-control form-control-sm mb-1"
                    />
                    <small className="text-muted d-block" style={{ fontSize: '0.78rem' }}>
                      📌 <strong>Note:</strong> Recommended resolution: <strong>200 × 200 px</strong> or <strong>250 × 250 px</strong> (Square PNG with Transparent BG).
                    </small>
                    {company.logo && (
                      <div className="mt-2 text-center">
                        <img
                          src={company.logo}
                          alt="Logo Preview"
                          className="rounded border p-1 bg-white"
                          style={{ maxHeight: "70px", maxWidth: "100%", objectFit: "contain" }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Right Side Illustration Image Upload */}
                  <div className="p-2 bg-light rounded border">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label small fw-bold mb-0 text-dark">
                        Header Right Side Image (Festival / Sparkler Graphic)
                      </label>
                      {company.rightImage && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={handleRemoveRightImage}
                          className="py-0 px-2 small"
                        >
                          🗑️ Remove Image
                        </Button>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleRightImageUpload}
                      className="form-control form-control-sm mb-1"
                    />
                    <small className="text-muted d-block" style={{ fontSize: '0.78rem' }}>
                      📌 <strong>Note:</strong> Recommended resolution: <strong>200 × 250 px</strong> or <strong>250 × 300 px</strong> (Portrait PNG with Transparent BG).
                    </small>
                    {company.rightImage && (
                      <div className="mt-2 text-center">
                        <img
                          src={company.rightImage}
                          alt="Right Image Preview"
                          className="rounded border p-1 bg-white"
                          style={{ maxHeight: "70px", maxWidth: "100%", objectFit: "contain" }}
                        />
                      </div>
                    )}
                  </div>
                </section>

                <section className="mb-4 p-3 bg-white rounded border shadow-sm">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="fw-bold mb-0 text-dark d-flex align-items-center gap-1">
                      🖼️ Background Image (optional)
                    </label>
                    {bgImageUrl && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleRemoveBgImage}
                        className="py-0 px-2 small"
                      >
                        🗑️ Remove Background
                      </Button>
                    )}
                  </div>
                  <input type="file" accept="image/*" onChange={handleBgUpload} className="form-control form-control-sm mb-2" />
                  {bgImageUrl && (
                    <div className="mt-2 text-center">
                      <img
                        src={bgImageUrl}
                        alt="bg preview"
                        className="rounded border shadow-sm"
                        style={{ maxHeight: '140px', maxWidth: '100%', objectFit: 'contain' }}
                      />
                    </div>
                  )}
                </section>

                {/* Custom / Edited Page Image Attachment */}
                <section className="mb-4 p-3 bg-white rounded border shadow-sm">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="fw-bold mb-0 text-dark d-flex align-items-center gap-1">
                      🎨 Attach Edited Page Image
                    </label>
                    {customPageConfig.imageSrc && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleRemoveCustomPageImage}
                        className="py-0 px-2 small"
                      >
                        🗑️ Remove
                      </Button>
                    )}
                  </div>
                  <p className="text-muted small mb-2">
                    Upload an edited page image (e.g. with GPay number or custom AI design added) to merge into the final PDF:
                  </p>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCustomPageImageUpload}
                    className="form-control form-control-sm mb-2"
                  />

                  {customPageConfig.imageSrc && (
                    <div className="p-2 bg-light rounded border mt-2">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span className="badge bg-success">✓ Custom Page Attached</span>
                      </div>

                      <div className="mb-2">
                        <label className="form-label small fw-bold mb-1">Insertion Option:</label>
                        <Form.Select
                          size="sm"
                          value={customPageConfig.mode}
                          onChange={(e) => setCustomPageConfig({ ...customPageConfig, mode: e.target.value })}
                        >
                          <option value="replace_last">Replace Last Page (Default)</option>
                          <option value="replace_num">Replace Specific Page Number</option>
                          <option value="append_end">Append as Extra Page at End</option>
                        </Form.Select>
                      </div>

                      {customPageConfig.mode === "replace_num" && (
                        <div className="mb-2">
                          <label className="form-label small mb-1">Page Number to Replace:</label>
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={customPageConfig.pageNum}
                            onChange={(e) => setCustomPageConfig({ ...customPageConfig, pageNum: Number(e.target.value) })}
                            className="form-control form-control-sm"
                          />
                        </div>
                      )}

                      <div className="mt-2 text-center">
                        <img
                          src={customPageConfig.imageSrc}
                          alt="Custom Edited Page Preview"
                          className="rounded border shadow-sm"
                          style={{ maxHeight: "140px", maxWidth: "100%", objectFit: "contain" }}
                        />
                      </div>
                    </div>
                  )}
                </section>

                <section className="mb-6 p-4 bg-gray-100 rounded-lg shadow">
                  <Row className="items-center mb-3">
                    <Col>
                      <label className="block font-medium text-lg">PDF Theme / CSS Adjustments</label>
                    </Col>
                    <Col>
                      <button
                        onClick={resetTheme}
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 reset"
                        data-tooltip-id="reset-tooltip"
                        data-tooltip-content="Reset to default theme settings"
                      >
                        Reset
                      </button>
                    </Col>
                    <Tooltip id="reset-tooltip" />
                  </Row>

                  <div className="mb-3 p-3 bg-white rounded border shadow-sm">
                    <Row className="g-3">
                      <Col md={12} className="mb-3">
                        {/* 🎨 Theme Mode Selector: Theme 1 vs Theme 2 */}
                        <div className="p-3 bg-white border rounded shadow-sm mb-2">
                          <h6 className="fw-bold text-dark mb-2">🎨 Choose Catalog Theme Mode</h6>
                          <Row className="g-2">
                            <Col md={6}>
                              <div
                                className={`p-3 rounded border ${!theme.enableTheme2 ? "border-primary bg-primary bg-opacity-10 shadow-sm" : "bg-light"}`}
                                onClick={() => setTheme((prev) => ({ ...prev, enableTheme2: false, hideFooter: false }))}
                                style={{ cursor: 'pointer' }}
                              >
                                <div className="fw-bold text-dark mb-1">Theme 1 (Standard)</div>
                                <small className="text-muted d-block" style={{ fontSize: '11px' }}>
                                  Predefined header on Page 1, background image on all table pages, footer visible.
                                </small>
                              </div>
                            </Col>
                            <Col md={6}>
                              <div
                                className={`p-3 rounded border ${theme.enableTheme2 ? "border-primary bg-primary bg-opacity-10 shadow-sm" : "bg-light"}`}
                                onClick={async () => {
                                  let extractedColor = theme.extractedBgColor;
                                  if (bgImageUrl && !extractedColor) {
                                    extractedColor = await extractImageColor(bgImageUrl);
                                  }
                                  setTheme((prev) => ({
                                    ...prev,
                                    enableTheme2: true,
                                    hideFooter: true,
                                    extractedBgColor: extractedColor || prev.extractedBgColor || "#f1f5f9",
                                    pageBackground: extractedColor || prev.extractedBgColor || prev.pageBackground || "#f1f5f9",
                                    tableBg: extractedColor || prev.extractedBgColor || prev.tableBg || "#f1f5f9",
                                  }));
                                  NotifyData("Theme 2 Enabled: Cover Page + Extracted BG Color + No Footer!", "success");
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                <div className="fw-bold text-dark mb-1">Theme 2 (Cover Image + Analyzed BG Color)</div>
                                <small className="text-muted d-block" style={{ fontSize: '11px' }}>
                                  Full cover image on Page 1, extracted image color on table pages, footer hidden on all pages.
                                </small>
                              </div>
                            </Col>
                          </Row>

                          {theme.enableTheme2 && (
                            <div className="mt-3 p-2 bg-light rounded border d-flex align-items-center justify-content-between">
                              <div>
                                <span className="fw-bold text-dark small me-2">🎨 Theme 2 Active</span>
                                <span className="badge bg-secondary me-2">Footer Hidden</span>
                                {theme.extractedBgColor && (
                                  <span className="badge text-dark border" style={{ backgroundColor: theme.extractedBgColor }}>
                                    Extracted Color: {theme.extractedBgColor}
                                  </span>
                                )}
                              </div>
                              {bgImageUrl && (
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  style={{ padding: '2px 8px', fontSize: '11px' }}
                                  onClick={async () => {
                                    const extracted = await extractImageColor(bgImageUrl);
                                    if (extracted) {
                                      setTheme((prev) => ({
                                        ...prev,
                                        extractedBgColor: extracted,
                                        pageBackground: extracted,
                                        tableBg: extracted,
                                      }));
                                      NotifyData(`Extracted Color (${extracted}) set as page background!`, "success");
                                    } else {
                                      NotifyData("Could not extract color from image", "warning");
                                    }
                                  }}
                                >
                                  🔍 Re-analyze Image Color
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </Col>

                      <Col md={6} className="mb-2">
                        <div
                          className={`p-3 border rounded h-100 ${theme.enableTheme1Header && !theme.enableTheme2 ? "border-primary bg-primary bg-opacity-10 shadow-sm" : "bg-light"}`}
                          style={{ cursor: theme.enableTheme2 ? 'not-allowed' : 'pointer' }}
                          onClick={() => {
                            if (!theme.enableTheme2) {
                              setTheme((prev) => ({ ...prev, enableTheme1Header: !prev.enableTheme1Header }));
                            }
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <Form.Check
                              type="switch"
                              id="enableTheme1Header-switch"
                              label={<span className="fw-bold text-primary">Enable Theme 1 Predefined Header</span>}
                              checked={Boolean(theme.enableTheme1Header && !theme.enableTheme2)}
                              onChange={(e) => {
                                e.stopPropagation();
                                setTheme((prev) => ({ ...prev, enableTheme1Header: e.target.checked }));
                              }}
                              disabled={Boolean(theme.enableTheme2)}
                              style={{ cursor: 'pointer' }}
                            />
                            <span className={`badge flex-shrink-0 ms-2 ${theme.enableTheme1Header && !theme.enableTheme2 ? "bg-success" : "bg-secondary"}`}>
                              {theme.enableTheme1Header && !theme.enableTheme2 ? "ON" : "OFF"}
                            </span>
                          </div>
                          <small className="text-muted d-block ms-4" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                            Shows top brand header on Page 1 ONLY & hides footer.
                          </small>
                        </div>
                      </Col>

                      <Col md={6} className="mb-2">
                        <div
                          className={`p-3 border rounded h-100 ${theme.enableSeparateTamilColumn ? "border-primary bg-primary bg-opacity-10 shadow-sm" : "bg-light"}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setTheme((prev) => ({ ...prev, enableSeparateTamilColumn: !prev.enableSeparateTamilColumn }))}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <Form.Check
                              type="switch"
                              id="enableSeparateTamilColumn-switch"
                              label={<span className="fw-bold text-dark">Separate Tamil Column (பொருள் பெயர்)</span>}
                              checked={Boolean(theme.enableSeparateTamilColumn)}
                              onChange={(e) => {
                                e.stopPropagation();
                                setTheme((prev) => ({ ...prev, enableSeparateTamilColumn: e.target.checked }));
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                            <span className={`badge flex-shrink-0 ms-2 ${theme.enableSeparateTamilColumn ? "bg-success" : "bg-secondary"}`}>
                              {theme.enableSeparateTamilColumn ? "ON" : "OFF"}
                            </span>
                          </div>
                          <small className="text-muted d-block ms-4" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                            Displays Tamil product name in its own dedicated column.
                          </small>
                        </div>
                      </Col>

                      <Col md={6} className="mb-2">
                        <div
                          className={`p-3 border rounded h-100 ${theme.showCategoryDiscountText !== false ? "border-primary bg-primary bg-opacity-10 shadow-sm" : "bg-light"}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setTheme((prev) => ({ ...prev, showCategoryDiscountText: prev.showCategoryDiscountText === false }))}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <Form.Check
                              type="switch"
                              id="showCategoryDiscountText-switch"
                              label={<span className="fw-semibold text-dark">Category Discount Label (Discount: X%)</span>}
                              checked={theme.showCategoryDiscountText !== false}
                              onChange={(e) => {
                                e.stopPropagation();
                                setTheme((prev) => ({ ...prev, showCategoryDiscountText: e.target.checked }));
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                            <span className={`badge flex-shrink-0 ms-2 ${theme.showCategoryDiscountText !== false ? "bg-success" : "bg-secondary"}`}>
                              {theme.showCategoryDiscountText !== false ? "SHOW" : "HIDE"}
                            </span>
                          </div>
                          <small className="text-muted d-block ms-4" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                            Show or hide discount label text on category header banners.
                          </small>
                        </div>
                      </Col>

                      <Col md={6} className="mb-2">
                        <div
                          className={`p-3 border rounded h-100 ${theme.enableAltRow ? "border-primary bg-primary bg-opacity-10 shadow-sm" : "bg-light"}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setTheme((prev) => ({ ...prev, enableAltRow: !prev.enableAltRow }))}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <Form.Check
                              type="switch"
                              id="enableAltRow-switch"
                              label={<span className="fw-semibold text-dark">Dual-Color Alternate Rows</span>}
                              checked={Boolean(theme.enableAltRow)}
                              onChange={(e) => {
                                e.stopPropagation();
                                setTheme((prev) => ({ ...prev, enableAltRow: e.target.checked }));
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                            <span className={`badge flex-shrink-0 ms-2 ${theme.enableAltRow ? "bg-success" : "bg-secondary"}`}>
                              {theme.enableAltRow ? "ON" : "OFF"}
                            </span>
                          </div>
                          <small className="text-muted d-block ms-4" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                            Enable alternating tint on table rows.
                          </small>
                        </div>
                      </Col>

                      <Col md={6} className="mb-2">
                        <div
                          className={`p-3 border rounded h-100 ${theme.hideDiscountRate ? "border-danger bg-danger bg-opacity-10 shadow-sm" : "bg-light"}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setTheme((prev) => ({ ...prev, hideDiscountRate: !prev.hideDiscountRate }))}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <Form.Check
                              type="switch"
                              id="hideDiscountRate-switch"
                              label={<span className="fw-semibold text-dark">Hide ₹Discount Rate</span>}
                              checked={Boolean(theme.hideDiscountRate)}
                              onChange={(e) => {
                                e.stopPropagation();
                                setTheme((prev) => ({ ...prev, hideDiscountRate: e.target.checked }));
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                            <span className={`badge flex-shrink-0 ms-2 ${theme.hideDiscountRate ? "bg-danger" : "bg-secondary"}`}>
                              {theme.hideDiscountRate ? "HIDDEN" : "VISIBLE"}
                            </span>
                          </div>
                          <small className="text-muted d-block ms-4" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                            Hides discount column & adjusts layout.
                          </small>
                        </div>
                      </Col>

                      <Col md={6} className="mb-2">
                        <div
                          className={`p-3 border rounded h-100 ${theme.hideRsPs ? "border-danger bg-danger bg-opacity-10 shadow-sm" : "bg-light"}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setTheme((prev) => ({ ...prev, hideRsPs: !prev.hideRsPs, showRequirement: false }))}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <Form.Check
                              type="switch"
                              id="hideRsPs-switch"
                              label={<span className="fw-semibold text-dark">Hide Rs & Ps Columns</span>}
                              checked={Boolean(theme.hideRsPs)}
                              onChange={(e) => {
                                e.stopPropagation();
                                setTheme((prev) => ({ ...prev, hideRsPs: e.target.checked, showRequirement: false }));
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                            <span className={`badge flex-shrink-0 ms-2 ${theme.hideRsPs ? "bg-danger" : "bg-secondary"}`}>
                              {theme.hideRsPs ? "HIDDEN" : "VISIBLE"}
                            </span>
                          </div>
                          <small className="text-muted d-block ms-4" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                            Hides Rs & Ps columns & auto-adjusts layout.
                          </small>
                        </div>
                      </Col>

                      <Col md={6} className="mb-2">
                        <div
                          className={`p-3 border rounded h-100 ${theme.showRequirement ? "border-primary bg-primary bg-opacity-10 shadow-sm" : "bg-light"}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setTheme((prev) => ({ ...prev, showRequirement: !prev.showRequirement, hideRsPs: false }))}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <Form.Check
                              type="switch"
                              id="showRequirement-switch"
                              label={<span className="fw-semibold text-dark">Requirement Mode (Single Box)</span>}
                              checked={Boolean(theme.showRequirement)}
                              onChange={(e) => {
                                e.stopPropagation();
                                setTheme((prev) => ({ ...prev, showRequirement: e.target.checked, hideRsPs: false }));
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                            <span className={`badge flex-shrink-0 ms-2 ${theme.showRequirement ? "bg-success" : "bg-secondary"}`}>
                              {theme.showRequirement ? "ON" : "OFF"}
                            </span>
                          </div>
                          <small className="text-muted d-block ms-4" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                            Merges Rs & Ps into one single "Requirement" box.
                          </small>
                        </div>
                      </Col>

                      {theme.enableTheme1Header && !theme.enableTheme2 && (
                        <Col md={12} className="mt-2 pt-2 border-top">
                          <Row className="align-items-center">
                            <Col md={4}>
                              <label className="form-label small fw-bold mb-0">Header Badge Text:</label>
                            </Col>
                            <Col md={8}>
                              <input
                                type="text"
                                value={theme.pricelistTitle || "// PRICELIST - 2026 //"}
                                onChange={(e) => setTheme({ ...theme, pricelistTitle: e.target.value })}
                                className="form-control form-control-sm"
                                placeholder="// PRICELIST - 2026 //"
                              />
                            </Col>
                          </Row>
                        </Col>
                      )}
                    </Row>
                  </div>

                  {/* 🎨 Predefined Double Color Themes */}
                  <div className="dual-themes-container shadow-sm mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h6 className="fw-bold m-0 text-dark">
                        🎨 Predefined Double Color Themes
                      </h6>
                      <small className="text-muted">Click preset for instant 1-click preview</small>
                    </div>

                    <div className="dual-theme-grid">
                      {PREDEFINED_DUAL_THEMES.map((preset) => {
                        const isActive =
                          theme.categoryBg?.toLowerCase() === preset.categoryBg.toLowerCase() &&
                          theme.categoryText?.toLowerCase() === preset.categoryText.toLowerCase();

                        return (
                          <div
                            key={preset.id}
                            className={`dual-theme-card ${isActive ? "active" : ""}`}
                            onClick={() => applyDualColorTheme(preset)}
                            title={preset.name}
                          >
                            <div className="dual-swatch-box">
                              <div
                                className="dual-swatch-primary"
                                style={{ backgroundColor: preset.primary, color: "#fff" }}
                              >
                                Primary
                              </div>
                              <div
                                className="dual-swatch-secondary"
                                style={{ backgroundColor: preset.secondary, color: preset.primary }}
                              >
                                Text
                              </div>
                            </div>
                            <div className="dual-theme-title">{preset.name}</div>
                          </div>
                        );
                      })}
                    </div>

                    {/* 🛠️ Custom Two Color Picker Controls */}
                    <div className="custom-dual-picker-box mt-3">
                      <h6 className="fw-bold mb-2 text-dark" style={{ fontSize: '0.9rem' }}>
                        🛠️ Custom Two-Color Chooser
                      </h6>
                      <Row className="g-2 items-center">
                        <Col md={5}>
                          <label className="block text-xs font-semibold mb-1 text-slate-700">
                            Color 1 (Primary / Header & Category BG)
                          </label>
                          <div className="d-flex align-items-center gap-2">
                            <input
                              type="color"
                              value={isValidHex(customPrimary) ? customPrimary : "#000000"}
                              onChange={(e) => {
                                const newPrimary = e.target.value;
                                setCustomPrimary(newPrimary);
                                applyCustomDualColors(newPrimary, customSecondary);
                              }}
                              className="color-swatch-input"
                            />
                            <input
                              type="text"
                              value={customPrimary}
                              onChange={(e) => {
                                const newPrimary = e.target.value;
                                setCustomPrimary(newPrimary);
                                if (isValidHex(newPrimary)) {
                                  applyCustomDualColors(newPrimary, customSecondary);
                                }
                              }}
                              className="p-1 rounded border text-sm w-100"
                              placeholder="#000000"
                            />
                          </div>
                        </Col>

                        <Col md={5}>
                          <label className="block text-xs font-semibold mb-1 text-slate-700">
                            Color 2 (Secondary / Text & Accent)
                          </label>
                          <div className="d-flex align-items-center gap-2">
                            <input
                              type="color"
                              value={isValidHex(customSecondary) ? customSecondary : "#FFD54F"}
                              onChange={(e) => {
                                const newSec = e.target.value;
                                setCustomSecondary(newSec);
                                applyCustomDualColors(customPrimary, newSec);
                              }}
                              className="color-swatch-input"
                            />
                            <input
                              type="text"
                              value={customSecondary}
                              onChange={(e) => {
                                const newSec = e.target.value;
                                setCustomSecondary(newSec);
                                if (isValidHex(newSec)) {
                                  applyCustomDualColors(customPrimary, newSec);
                                }
                              }}
                              className="p-1 rounded border text-sm w-100"
                              placeholder="#FFD54F"
                            />
                          </div>
                        </Col>

                        <Col md={2} className="d-flex align-items-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary w-100 mt-3 mt-md-0"
                            onClick={() => applyCustomDualColors(customPrimary, customSecondary)}
                          >
                            Apply
                          </button>
                        </Col>
                      </Row>
                    </div>
                  </div>

                  <Row className="grid grid-cols-2 gap-4">
                    <Row>
                      <Col>
                        <label className="block text-sm">Page Margin (px)</label>
                      </Col>
                      <Col>
                        <input
                          type="number"
                          value={theme.pageMargin}
                          onChange={(e) => setTheme({ ...theme, pageMargin: Number(e.target.value) })}
                          min="0"
                          max="100"
                          className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Page margin"
                        />
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <label className="block text-sm">Font Size (pt)</label>
                      </Col>
                      <Col>
                        <input
                          type="number"
                          value={theme.fontSize}
                          onChange={(e) => setTheme({ ...theme, fontSize: Number(e.target.value) })}
                          min="8"
                          max="20"
                          className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Font size"
                        />
                      </Col>
                    </Row>

                    <div className="p-3 bg-light rounded border mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <label className="fw-bold text-dark m-0 small">🎯 Target Total Pages (Auto-Fit)</label>
                        {theme.targetPages ? (
                          <span className="badge bg-success small">Target: {theme.targetPages} Pages</span>
                        ) : (
                          <span className="badge bg-secondary small">Manual</span>
                        )}
                      </div>
                      <p className="text-muted extra-small mb-2" style={{ fontSize: '11px' }}>
                        Click target page count to auto-adjust product limits and fit all items without white-page overflow:
                      </p>
                      <div className="d-flex align-items-center gap-1 flex-wrap mb-2">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                          <Button
                            key={num}
                            variant={theme.targetPages === num ? "primary" : "outline-primary"}
                            size="sm"
                            style={{ padding: '2px 8px', fontSize: '12px' }}
                            onClick={() => autoFitToTargetPages(num)}
                          >
                            {num} {num === 1 ? 'Page' : 'Pgs'}
                          </Button>
                        ))}
                        {theme.targetPages && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            style={{ padding: '2px 6px', fontSize: '11px' }}
                            onClick={() => setTheme({ ...theme, targetPages: null })}
                          >
                            Reset Manual
                          </Button>
                        )}
                      </div>
                      {theme.targetPages && (
                        <div className="text-success fw-semibold extra-small" style={{ fontSize: '11px' }}>
                          ✨ Auto-fitted! Page 1: {theme.firstPageProducts} items | Other Pages: {theme.productsPerPage} items/page
                        </div>
                      )}
                    </div>

                    <Row>
                      <Col>
                        <label className="block text-sm">Page 1 Products Limit</label>
                      </Col>
                      <Col>
                        <input
                          type="number"
                          value={theme.firstPageProducts !== undefined ? theme.firstPageProducts : 24}
                          onChange={(e) => setTheme({ ...theme, targetPages: null, firstPageProducts: Number(e.target.value) })}
                          min="10"
                          max="40"
                          className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Page 1 limit"
                        />
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <label className="block text-sm">Other Pages Limit</label>
                      </Col>
                      <Col>
                        <input
                          type="number"
                          value={theme.productsPerPage || 32}
                          onChange={(e) => setTheme({ ...theme, targetPages: null, productsPerPage: Number(e.target.value) })}
                          min="10"
                          max="50"
                          className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Other pages limit"
                        />
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <label className="block text-sm font-semibold text-primary">🖼️ Page Background Color</label>
                      </Col>
                      <Col>
                        <div className="relative">
                          <div className="d-flex align-items-center gap-2">
                            <input
                              type="color"
                              value={isValidHex(theme.pageBackground) ? theme.pageBackground : "#ffffff"}
                              onChange={(e) => {
                                const val = e.target.value;
                                setTheme((prev) => ({
                                  ...prev,
                                  pageBackground: val,
                                  extractedBgColor: val,
                                  ...(prev.enableTheme2 ? { tableBg: val } : {}),
                                }));
                              }}
                              className="color-swatch-input"
                            />
                            <input
                              value={theme.pageBackground || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                setTheme((prev) => ({
                                  ...prev,
                                  pageBackground: value,
                                  extractedBgColor: value,
                                  ...(prev.enableTheme2 ? { tableBg: value } : {}),
                                }));
                              }}
                              onFocus={() => setShowColorPickerPageBackground(true)}
                              className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500 w-100"
                              placeholder="Page BG (hex/name)"
                            />
                          </div>
                          {showColorPickerPageBackground && (
                            <div className="absolute z-10 mt-2">
                              <SketchPicker
                                color={isValidHex(theme.pageBackground) ? theme.pageBackground : '#ffffff'}
                                onChangeComplete={handleColorChangePageBg}
                              />
                              <button
                                onClick={() => setShowColorPickerPageBackground(false)}
                                className="mt-2 px-3 py-1 bg-gray-300 rounded text-xs"
                              >
                                Close
                              </button>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <label className="block text-sm">Category Background Color</label>
                      </Col>
                      <Col>
                        <div className="relative">
                          <input
                            value={theme.categoryBg}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (isValidHex(value) || value === '') {
                                setTheme({ ...theme, categoryBg: value });
                              }
                            }}
                            onFocus={() => setShowColorPicker(true)}
                            className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Category BG (hex)"
                          />
                          {showColorPicker && (
                            <div className="absolute z-10 mt-2">
                              <SketchPicker color={theme.categoryBg} onChangeComplete={handleColorChangeCategoryBg} />
                              <button
                                onClick={() => setShowColorPicker(false)}
                                className="mt-2 px-3 py-1 bg-gray-300 rounded"
                              >
                                Close
                              </button>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row className="flex">
                      <Col>
                        <label className="block text-sm">Category Text Color</label>
                      </Col>
                      <Col>
                        <div className="relative">
                          <input
                            value={theme.categoryText}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (isValidHex(value) || value === '') {
                                setTheme({ ...theme, categoryText: value });
                              }
                            }}
                            onFocus={() => setShowColorPickercategorytext(true)}
                            className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Category Text (hex)"
                          />
                          {showColorPickercategorytext && (
                            <div className="absolute z-10 mt-2">
                              <SketchPicker color={theme.categoryText} onChangeComplete={handleColorChangecategorytext} />
                              <button
                                onClick={() => setShowColorPickercategorytext(false)}
                                className="mt-2 px-3 py-1 bg-gray-300 rounded"
                              >
                                Close
                              </button>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <label className="block text-sm">Header Background Color</label>
                      </Col>
                      <Col>
                        <div className="relative">
                          <input
                            value={theme.headerbackground}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (isValidHex(value) || value === '') {
                                setTheme({ ...theme, headerbackground: value });
                              }
                            }}
                            onFocus={() => setShowColorPickerheaderback(true)}
                            className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Header background (hex)"
                          />
                          {showColorPickerheaderback && (
                            <div className="absolute z-10 mt-2">
                              <SketchPicker color={theme.headerbackground} onChangeComplete={handleColorChangeheaderbackground} />
                              <button
                                onClick={() => setShowColorPickerheaderback(false)}
                                className="mt-2 px-3 py-1 bg-gray-300 rounded"
                              >
                                Close
                              </button>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <label className="block text-sm">Header Text Color</label>
                      </Col>
                      <Col>
                        <div className="relative">
                          <input
                            value={theme.headertext}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (isValidHex(value) || value === '') {
                                setTheme({ ...theme, headertext: value });
                              }
                            }}
                            onFocus={() => setShowColorPickerheader(true)}
                            className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Header Text (hex)"
                          />
                          {showColorPickerheader && (
                            <div className="absolute z-10 mt-2">
                              <SketchPicker color={theme.headertext} onChangeComplete={handleColorChangeheadertext} />
                              <button
                                onClick={() => setShowColorPickerheader(false)}
                                className="mt-2 px-3 py-1 bg-gray-300 rounded"
                              >
                                Close
                              </button>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <label className="block text-sm">Footer Background Color</label>
                      </Col>
                      <Col>
                        <div className="relative">
                          <input
                            value={theme.footerBg}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (isValidHex(value) || value === '') {
                                setTheme({ ...theme, footerBg: value });
                              }
                            }}
                            onFocus={() => setShowColorPickerfooterbackground(true)}
                            className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Footer BG (hex)"
                          />
                          {showColorPickerfooterbackground && (
                            <div className="absolute z-10 mt-2">
                              <SketchPicker color={theme.footerBg} onChangeComplete={handleColorChangefooterbackground} />
                              <button
                                onClick={() => setShowColorPickerfooterbackground(false)}
                                className="mt-2 px-3 py-1 bg-gray-300 rounded"
                              >
                                Close
                              </button>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <label className="block text-sm">Footer Text Color</label>
                      </Col>
                      <Col>
                        <div className="relative">
                          <input
                            value={theme.footerText}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (isValidHex(value) || value === '') {
                                setTheme({ ...theme, footerText: value });
                              }
                            }}
                            onFocus={() => setShowColorPickerfootertext(true)}
                            className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Footer Text (hex)"
                          />
                          {showColorPickerfootertext && (
                            <div className="absolute z-10 mt-2">
                              <SketchPicker color={theme.footerText} onChangeComplete={handleColorChangefootertext} />
                              <button
                                onClick={() => setShowColorPickerfootertext(false)}
                                className="mt-2 px-3 py-1 bg-gray-300 rounded"
                              >
                                Close
                              </button>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <label className="block text-sm">Table Background Color</label>
                      </Col>
                      <Col>
                        <div className="relative">
                          <input
                            value={theme.tableBg}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (isValidHex(value) || value === '') {
                                setTheme({ ...theme, tableBg: value });
                              }
                            }}
                            onFocus={() => setShowColorPickerTableBackground(true)}
                            className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Table BG (hex)"
                          />
                          {showColorPickerTableBackground && (
                            <div className="absolute z-10 mt-2">
                              <SketchPicker color={theme.tableBg} onChangeComplete={handleColorChangeTableBackground} />
                              <button
                                onClick={() => setShowColorPickerTableBackground(false)}
                                className="mt-2 px-3 py-1 bg-gray-300 rounded"
                              >
                                Close
                              </button>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <label className="block text-sm">Table Border Color</label>
                      </Col>
                      <Col>
                        <div className="relative">
                          <input
                            value={theme.bordercolor}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (isValidHex(value) || value === '') {
                                setTheme({ ...theme, bordercolor: value });
                              }
                            }}
                            onFocus={() => setShowColorPickerbordercolor(true)}
                            className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Border Color (hex)"
                          />
                          {showColorPickerbordercolor && (
                            <div className="absolute z-10 mt-2">
                              <SketchPicker color={theme.bordercolor} onChangeComplete={handleColorChangebordercolor} />
                              <button
                                onClick={() => setShowColorPickerbordercolor(false)}
                                className="mt-2 px-3 py-1 bg-gray-300 rounded"
                              >
                                Close
                              </button>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <label className="block text-sm">Background Opacity (0-1)</label>
                      </Col>
                      <Col>
                        <input
                          type="number"
                          value={theme.bgOpacity}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 0 && value <= 1) {
                              setTheme({ ...theme, bgOpacity: value });
                            }
                          }}
                          step="0.05"
                          min="0"
                          max="1"
                          className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="BG opacity (0-1)"
                        />
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <label className="block text-sm">Products Per Page</label>
                      </Col>
                      <Col>
                        <input
                          type="number"
                          value={theme.productsPerPage}
                          onChange={(e) => setTheme({ ...theme, productsPerPage: Number(e.target.value) })}
                          min="10"
                          max="50"
                          className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Products per page"
                        />
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <label className="block text-sm">Header Font Size (pt)</label>
                      </Col>
                      <Col>
                        <input
                          type="number"
                          value={theme.headerFontSize}
                          onChange={(e) => setTheme({ ...theme, headerFontSize: Number(e.target.value) })}
                          min="10"
                          max="24"
                          className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Header font size"
                        />
                      </Col>
                    </Row>

                    <Row className="mb-2">
                      <Col xs={12}>
                        <Form.Check
                          type="checkbox"
                          id="chk-show-bg-all"
                          label="⚡ Ultra Fast Mobile Scroll (Background on Cover Page only)"
                          checked={!theme.showBgOnAllPages}
                          onChange={(e) => setTheme({ ...theme, showBgOnAllPages: !e.target.checked })}
                          className="text-sm font-medium text-gray-700"
                        />
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <label className="block text-sm">Page Orientation</label>
                      </Col>
                      <Col>
                        <select
                          value={theme.pageOrientation}
                          onChange={(e) => setTheme({ ...theme, pageOrientation: e.target.value })}
                          className="p-2 rounded border innvl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="portrait">Portrait</option>
                          <option value="landscape">Landscape</option>
                        </select>
                      </Col>
                    </Row>
                  </Row>

                  <Tooltip id="preview-tooltip" />
                </section>

                <section className="mt-5">
                  <div className="d-flex justify-content-between">
                    <button
                      onClick={handleDownloadPdf}
                      className="py-2 px-4 dwnld-btn"
                    >
                      Download PDF
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={isUploading}
                      className="py-2 px-2 sut-btn"
                    >
                      {isUploading ? 'Saving...' : 'Submit & Save'}
                    </button>
                  </div>

                  {result && (
                    <div style={{ marginTop: 8, padding: 8, border: `1px solid ${result.ok ? '#16a34a' : '#ef4444'}` }}>
                      <pre style={{ fontSize: 12 }}>{JSON.stringify(result, null, 2)}</pre>
                    </div>
                  )}
                </section>
              </div>

              {/* RIGHT: PDF Viewer */}
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, overflow: "hidden" }}>
                <PDFViewer
                  key={`pdf-preview-${JSON.stringify(theme)}-${bgImageUrl || 'nobg'}-${company.logo || ''}-${company.rightImage || ''}-${company.name || ''}-${customPageConfig.imageSrc || ''}-${customPageConfig.mode || ''}-${categoryRows.length}`}
                  width="100%"
                  height="100%"
                >
                  <BillFour
                    products={productRows ?? []}
                    categoryRows={categoryRows}
                    categoryName={selectedCategory || "All Products"}
                    categoryDiscount={categoryRows.find(c => c.category_name === selectedCategory)?.discount || 0}
                    company={company}
                    bgImageSrc={bgImageUrl}
                    theme={theme}
                    groupedProducts={groupedProducts} // Pass grouped products to BillFour
                    customPageConfig={customPageConfig}
                  />
                </PDFViewer>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Multi-Select Page Image Export Modal */}
      <Modal show={showPageExportModal} onHide={() => setShowPageExportModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h6 mb-0 text-dark fw-bold">
            🖼️ Export PDF Pages as Images (PNG)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="text-muted small mb-3">
            Total Pages in Design: <strong>{totalPagesCount} Pages</strong>. Select the pages you want to download as high-resolution PNG images (ideal for adding GPay numbers or custom designs in Canva / Photoshop / ChatGPT):
          </p>

          {/* Quick Action Selection Bar */}
          <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setSelectedPages(Array.from({ length: totalPagesCount }, (_, i) => i + 1))}
            >
              Select All ({totalPagesCount})
            </Button>
            <Button
              variant="outline-success"
              size="sm"
              onClick={() => setSelectedPages([totalPagesCount])}
            >
              Last Page Only (Page {totalPagesCount})
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setSelectedPages([])}
            >
              Deselect All
            </Button>
          </div>

          {/* Page Checkbox Grid */}
          <div className="p-3 bg-light rounded border" style={{ maxHeight: "280px", overflowY: "auto" }}>
            <Row className="g-2">
              {Array.from({ length: totalPagesCount }, (_, i) => i + 1).map((pageNum) => {
                const isChecked = selectedPages.includes(pageNum);
                const isLastPage = pageNum === totalPagesCount;
                return (
                  <Col key={pageNum} xs={6} sm={4} md={3}>
                    <div
                      className={`p-2.5 rounded border text-center ${isChecked ? "bg-primary text-white border-primary shadow-sm" : "bg-white text-dark"
                        }`}
                      style={{ cursor: "pointer", transition: "all 0.2s ease" }}
                      onClick={() => {
                        if (isChecked) {
                          setSelectedPages(selectedPages.filter((p) => p !== pageNum));
                        } else {
                          setSelectedPages([...selectedPages, pageNum].sort((a, b) => a - b));
                        }
                      }}
                    >
                      <Form.Check
                        type="checkbox"
                        id={`page-chk-${pageNum}`}
                        label={`Page ${pageNum} ${isLastPage ? "(Last Page)" : ""}`}
                        checked={isChecked}
                        onChange={() => { }}
                        className="mb-0 small fw-semibold"
                      />
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" size="sm" onClick={() => setShowPageExportModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleDownloadSelectedPages}
            disabled={isExportingImages || selectedPages.length === 0}
          >
            {isExportingImages ? "Generating PNG Images..." : `📥 Download Selected (${selectedPages.length}) Images`}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}